import { Controller } from 'stimulus'

const P = 'lexiqamus-user-guide-page'
const sel = (name) => `.${P}-${name}`

// User guide only — screenshot lightbox (<dialog>) + in-page jump-back. Back-to-top is CSS-only (_back_to_top.html.erb).
export default class extends Controller {
  static targets = ['lightbox', 'lightboxContent', 'jumpBack']

  connect () {
    this.jumpFrom = null
    this.jumpHost = null
    this.jumpTimer = null
    this.jumpTargetTimer = null
    if (this.hasLightboxTarget) {
      this.element.addEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.addEventListener('click', this.onLightboxClick)
      this.lightboxTarget.addEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.addEventListener('keydown', this.onKeydown)
      document.addEventListener('click', this.onDismissJump, true)
      document.addEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.addEventListener(ev, this.onDismissJump, { passive: true, capture: true })
      }
    }
  }

  disconnect () {
    if (this.hasLightboxTarget) {
      this.element.removeEventListener('click', this.onScreenshotClick)
      this.lightboxTarget.removeEventListener('click', this.onLightboxClick)
      this.lightboxTarget.removeEventListener('close', this.onLightboxClosed)
    }

    if (this.hasJumpBackTarget) {
      document.removeEventListener('keydown', this.onKeydown)
      document.removeEventListener('click', this.onDismissJump, true)
      document.removeEventListener('click', this.onAnchorClick)
      for (const ev of ['mousedown', 'wheel', 'touchstart']) {
        document.removeEventListener(ev, this.onDismissJump, { capture: true })
      }
      this.detachJump()
    }
  }

  // Enlarge control is a native <button> in _user_guide_*.html.erb; keyboard activation needs no extra handlers.
  onScreenshotClick = (event) => {
    const trigger = event.target.closest('button.lexiqamus-user-guide-page-screenshot-enlarge')
    if (!trigger || !this.element.contains(trigger)) return
    const img = trigger.querySelector('img')
    if (!img || !this.hasLightboxTarget || !this.hasLightboxContentTarget) return

    const map = img.closest(sel('screenshot-map'))
    let node
    if (map) {
      // Overview figures: clone map + pins for the lightbox; unwrap enlarge buttons so the dialog is not interactive.
      node = map.cloneNode(true)
      node.querySelectorAll('button.lexiqamus-user-guide-page-screenshot-enlarge').forEach((btn) => {
        const cloneImg = btn.querySelector('img')
        btn.replaceWith(cloneImg)
      })
      const figure = map.closest('figure')
      if (figure) node.style.setProperty('--ar', figure.style.getPropertyValue('--ar'))
    } else {
      node = img.cloneNode(true)
    }

    this.lightboxContentTarget.replaceChildren(node)
    if (typeof this.lightboxTarget.showModal === 'function') {
      this.lightboxTarget.showModal()
    }
  }

  // Backdrop click only — content clicks must not close (native <dialog> in _page_lightbox.html.erb).
  onLightboxClick = (event) => {
    if (event.target === this.lightboxTarget && this.lightboxTarget.open) this.lightboxTarget.close()
  }

  // Escape / form method="dialog" close; browser restores focus to the enlarge control.
  onLightboxClosed = () => {
    if (this.hasLightboxContentTarget) this.lightboxContentTarget.replaceChildren()
  }

  onKeydown = (event) => {
    if (event.key !== 'Escape') return
    if (this.hasLightboxTarget && this.lightboxTarget.open) return
    this.jumpBack()
  }

  onAnchorClick = (event) => {
    const link = event.target.closest('a[href^="#"]')
    if (!link || !this.element.contains(link) || link.closest('.lexiqamus-page-toc, .lexiqamus-lq-backtop')) return

    const id = decodeURIComponent(link.getAttribute('href').slice(1))
    if (!id) return
    const target = document.getElementById(id)
    if (!target) return

    this.detachJump()
    this.jumpFrom = window.scrollY

    const head = target.querySelector('h2,h3,h4') ||
      (/^H[234]$/.test(target.tagName || '') ? target : (target.closest(sel('article')) || target))
    this.jumpHost = head
    this.jumpHost.classList.add(`${P}-jump-host`, `${P}-jump-target`)
    void this.jumpHost.offsetWidth
    this.jumpHost.appendChild(this.jumpBackTarget)
    this.jumpBackTarget.hidden = false
    requestAnimationFrame(() => this.jumpBackTarget.classList.add(`${P}-jump-back--visible`))
    this.autoHideJump()
    if (this.jumpTargetTimer) clearTimeout(this.jumpTargetTimer)
    this.jumpTargetTimer = setTimeout(() => {
      this.jumpHost?.classList.remove(`${P}-jump-target`)
      this.jumpTargetTimer = null
    }, 2400)
  }

  onDismissJump = (event) => {
    if (event?.target?.closest?.(sel('jump-back'))) return
    if (!this.jumpBackTarget.hidden) {
      this.jumpFrom = null
      this.detachJump()
    }
  }

  jumpBack () {
    if (this.jumpFrom == null) return
    window.scrollTo({ top: this.jumpFrom, behavior: 'smooth' })
    this.jumpFrom = null
    this.detachJump()
  }

  autoHideJump () {
    if (this.jumpTimer) clearTimeout(this.jumpTimer)
    this.jumpTimer = setTimeout(() => {
      this.jumpFrom = null
      this.detachJump()
    }, 5000)
  }

  detachJump () {
    if (this.hasJumpBackTarget) {
      this.jumpBackTarget.classList.remove(`${P}-jump-back--visible`)
      this.jumpBackTarget.hidden = true
      if (this.jumpBackTarget.parentElement !== this.element) this.element.appendChild(this.jumpBackTarget)
    }
    if (this.jumpHost) this.jumpHost.classList.remove(`${P}-jump-host`, `${P}-jump-target`)
    if (this.jumpTimer) {
      clearTimeout(this.jumpTimer)
      this.jumpTimer = null
    }
    if (this.jumpTargetTimer) {
      clearTimeout(this.jumpTargetTimer)
      this.jumpTargetTimer = null
    }
  }
}
